import { Form, Formik, useField, useFormikContext } from "formik";
import * as Yup from "yup";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { Client } from "@/features/clients/types";
import {
  DESIGNATION_OPTIONS,
  type Form2848Representative,
  type FormSubmission,
  type GenerateFor,
} from "@/features/forms/types";
import { FormEditorActions } from "./form-editor-actions";
import { FormikCell, FormikCheckbox, FormikSelect } from "./formik-controls";

// --- Form IL-2848 editable payload ------------------------------------------
// snake_case keys mirror `apps/forms/mappings/form_2848il.py` `build_defaults`
// exactly (the submission `data` blob is passed through verbatim). IL-2848 is
// the Illinois Power of Attorney: the base POA skeleton (taxpayer + reps) with
// NO tax-matter rows (Step 3 grants full authority), a POA `purpose` selector, a
// per-rep `il_jurisdiction`, a duplicate-notices toggle, and a Step-4
// authorized-agent block.

/** An IL-2848 representative — the base rep plus its Illinois licensing jurisdiction. */
export interface Form2848ilRepresentative extends Form2848Representative {
  il_jurisdiction: string;
}

/** POA purpose (Step "Check one box"). */
export type Il2848Purpose = "new" | "add" | "chg" | "del";

export interface Form_2848il_Data {
  generate_for: GenerateFor;
  representatives: Form2848ilRepresentative[];
  purpose: Il2848Purpose;
  send_dupli_copy1: boolean;
  // Step 4 — authorized agent (only prints when auth_agent is checked).
  auth_agent: boolean;
  auth_name: string;
  auth_title: string;
  auth_day_time_phone: string;
  auth_email: string;
  auth_street: string;
  auth_city: string;
  auth_state: string;
  auth_zip: string;
}

const PURPOSE_OPTIONS: { value: Il2848Purpose; label: string }[] = [
  { value: "new", label: "Add New POA" },
  { value: "add", label: "Additional POA" },
  { value: "chg", label: "Change Existing POA" },
  { value: "del", label: "Remove Existing POA" },
];

const GENERATE_FOR_OPTIONS: { value: GenerateFor; label: string }[] = [
  { value: "taxpayer", label: "Taxpayer" },
  { value: "spouse", label: "Spouse" },
  { value: "jointly", label: "Jointly" },
];

const schema = Yup.object({
  generate_for: Yup.string().oneOf(["taxpayer", "spouse", "jointly"]).required(),
  purpose: Yup.string().oneOf(["new", "add", "chg", "del"]).required(),
  auth_agent: Yup.boolean(),
  auth_name: Yup.string().when("auth_agent", {
    is: true,
    then: (s) => s.trim().required("Authorized agent name is required."),
  }),
});

function toInitialValues(data: Record<string, unknown>): Form_2848il_Data {
  const d = data as Partial<Form_2848il_Data>;
  return {
    generate_for: d.generate_for ?? "taxpayer",
    representatives: (d.representatives ?? []).map((r) => ({
      ...r,
      il_jurisdiction: r.il_jurisdiction ?? "",
    })),
    purpose: d.purpose ?? "new",
    send_dupli_copy1: d.send_dupli_copy1 ?? false,
    auth_agent: d.auth_agent ?? false,
    auth_name: d.auth_name ?? "",
    auth_title: d.auth_title ?? "",
    auth_day_time_phone: d.auth_day_time_phone ?? "",
    auth_email: d.auth_email ?? "",
    auth_street: d.auth_street ?? "",
    auth_city: d.auth_city ?? "",
    auth_state: d.auth_state ?? "",
    auth_zip: d.auth_zip ?? "",
  };
}

function GenerateForField() {
  const [field, , helpers] = useField<GenerateFor>("generate_for");
  return (
    <RadioGroup
      className="flex flex-wrap gap-6"
      value={field.value}
      onValueChange={(v) => helpers.setValue(v as GenerateFor)}
    >
      {GENERATE_FOR_OPTIONS.map((o) => (
        <Label key={o.value} htmlFor={`gf-${o.value}`} className="flex items-center gap-2 font-normal">
          <RadioGroupItem id={`gf-${o.value}`} value={o.value} />
          {o.label}
        </Label>
      ))}
    </RadioGroup>
  );
}

/**
 * The client's assigned representatives with an Illinois licensing jurisdiction
 * column. Only the first active rep prints on page 1; the rest are approximated
 * on an appended continuation sheet server-side (IL-2848-A stand-in).
 */
function Il2848RepresentativesTable() {
  const { values } = useFormikContext<Form_2848il_Data>();
  const reps = values.representatives;

  if (reps.length === 0) {
    return (
      <p className="text-muted-foreground text-sm">
        No representatives are assigned to this client. Add them on the client's
        profile, then reopen this form.
      </p>
    );
  }

  return (
    <Card className="gap-0 overflow-x-auto overflow-hidden p-0">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-16">Active</TableHead>
            <TableHead>Rep Name</TableHead>
            <TableHead className="w-48">Designation</TableHead>
            <TableHead className="w-48">Jurisdiction</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {reps.map((rep, i) => (
            <TableRow key={rep.practitioner || i}>
              <TableCell>
                <FormikCheckbox
                  name={`representatives.${i}.active`}
                  aria-label={`Active ${rep.name}`}
                />
              </TableCell>
              <TableCell className="font-medium">{rep.name}</TableCell>
              <TableCell>
                <FormikSelect
                  name={`representatives.${i}.designation`}
                  options={DESIGNATION_OPTIONS}
                  placeholder="Select…"
                  aria-label={`Designation for ${rep.name}`}
                />
              </TableCell>
              <TableCell>
                <FormikCell
                  name={`representatives.${i}.il_jurisdiction`}
                  aria-label={`Jurisdiction for ${rep.name}`}
                  placeholder="e.g. IL"
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

/** Step 4 — authorized agent (corporate officer / partner / individual /
 * fiduciary) executing on behalf of the taxpayer. Fields enable when checked. */
function AuthorizedAgentSection() {
  const { values } = useFormikContext<Form_2848il_Data>();
  return (
    <Card className="gap-4 p-6">
      <SectionHeading>Authorized Agent (Step 4)</SectionHeading>
      <FormikCheckbox
        name="auth_agent"
        label="An authorized agent or fiduciary is executing this form on behalf of the taxpayer"
      />
      {values.auth_agent && (
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label htmlFor="auth_name">Name</Label>
            <FormikCell name="auth_name" aria-label="Authorized agent name" />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="auth_title">Title</Label>
            <FormikCell name="auth_title" aria-label="Authorized agent title" />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="auth_day_time_phone">Daytime phone</Label>
            <FormikCell name="auth_day_time_phone" aria-label="Authorized agent phone" />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="auth_email">Email</Label>
            <FormikCell name="auth_email" type="email" aria-label="Authorized agent email" />
          </div>
          <div className="grid gap-2 sm:col-span-2">
            <Label htmlFor="auth_street">Street address</Label>
            <FormikCell name="auth_street" aria-label="Authorized agent street" />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="auth_city">City</Label>
            <FormikCell name="auth_city" aria-label="Authorized agent city" />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="auth_state">State</Label>
            <FormikCell name="auth_state" aria-label="Authorized agent state" />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="auth_zip">ZIP</Label>
            <FormikCell name="auth_zip" aria-label="Authorized agent zip" />
          </div>
        </div>
      )}
    </Card>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return <h3 className="text-foreground border-b pb-2 text-lg font-bold">{children}</h3>;
}

interface Props {
  client: Client;
  submission: FormSubmission;
  onSubmissionChange: (submission: FormSubmission) => void;
}

/**
 * Editable Form IL-2848 (Illinois Power of Attorney) — prefilled from the client
 * + its practitioners. IL-2848 has no tax-matter rows (Step 3 grants full
 * authority); instead it captures a POA purpose, a per-rep Illinois jurisdiction,
 * a duplicate-notices toggle, and an authorized-agent block. Save / Preview PDF /
 * Save to Documents via the shared action bar.
 */
export function Form2848ilEditor({ client, submission, onSubmissionChange }: Props) {
  const address = [
    client.homeStreet,
    [client.homeCity, [client.homeState, client.homeZip].filter(Boolean).join(" ")]
      .filter(Boolean)
      .join(", "),
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <Formik
      initialValues={toInitialValues(submission.data)}
      validationSchema={schema}
      onSubmit={() => {}}
    >
      <Form className="flex flex-col gap-6">
        <Card className="gap-6 p-6">
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-4">
              <span className="text-foreground font-semibold">Generate for</span>
              <GenerateForField />
            </div>
            <div className="grid gap-2 sm:max-w-xs">
              <Label htmlFor="purpose">Purpose of this POA</Label>
              <FormikSelect name="purpose" options={PURPOSE_OPTIONS} aria-label="POA purpose" />
            </div>
            <div className="text-muted-foreground text-sm">
              <p className="text-foreground font-medium">{client.name}</p>
              {address && <p>{address}</p>}
            </div>
          </div>
        </Card>

        <section className="flex flex-col gap-4">
          <SectionHeading>Power of Attorney Given To</SectionHeading>
          <Il2848RepresentativesTable />
          <FormikCheckbox
            name="send_dupli_copy1"
            label="Authorize IDOR to send duplicate copies of notices to the Power of Attorney"
          />
        </section>

        <AuthorizedAgentSection />

        <FormEditorActions
          submission={submission}
          clientId={client.id}
          onSubmissionChange={onSubmissionChange}
        />
      </Form>
    </Formik>
  );
}
